import React from "react";
import Portal from "react-portal"
import PersistentPortal from "react-persistent-portal";
import { findDOMNode } from "react-dom";
import classnames from "classnames";
import "./styles.scss";

// Note: this *isn't* the class that's exported.
// It's a helper class to work around https://github.com/tajo/react-portal/issues/2
class OverlayAwareModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tabFirstElem: null,
      tabLastElem: null,
    };

    this.isPersistent = typeof props.persistent !== 'undefined' ? props.persistent : false;

    this.trapFocus = this.trapFocus.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleMouseClickOutside = this.handleMouseClickOutside.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
  }

  componentDidMount() {
    if (this.props.closeOnOutsideClick && typeof document !== "undefined"){
      document.addEventListener('mousedown', this.handleMouseClickOutside); //eslint-disable-line no-undef
    }

    if (!this.isPersistent) {
      const modalContent = document.getElementById(this.props.id)

      // setTimeout needed to bypass modal CSS transition
      setTimeout(() => {
        this.trapFocus(modalContent)
      }, 1)
    }
  }

  componentWillUnmount() {
    if (this.props.closeOnOutsideClick && typeof document !== "undefined") {
      document.removeEventListener('mousedown', this.handleMouseClickOutside); //eslint-disable-line no-undef
    }

    if (!this.isPersistent) {
      document.removeEventListener('keydown', this.handleFocus);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.isPersistent) {
      if (!prevProps.isOpened && this.props.isOpened) {
        const modalContent = document.getElementById(this.props.id)
        this.trapFocus(modalContent)
      } else if (prevProps.isOpened && !this.props.isOpened) {
        document.removeEventListener('keydown', this.handleFocus);
      } 
    }
  }

  trapFocus(containerNode) {
    const selector = 'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select';
    const focusableEls = Array
                          .from(containerNode.querySelectorAll(selector))
                          .filter(e => !e.classList.contains('modal__noFocus'));

    // if only focusable is close, focus on close, else focus on second
    if (focusableEls.length === 1) {
      focusableEls[0].focus();
    } else {
      focusableEls[1].focus();
    }

    this.setState({
      tabFirstElem: focusableEls[0],
      tabLastElem: focusableEls[focusableEls.length - 1],
    }, () => {
      document.addEventListener('keydown', this.handleFocus)
    });
  }  

      
  handleFocus(e) {
    const { tabFirstElem, tabLastElem } = this.state;

    let KEYCODE_TAB = 9,
        isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB);
    
    if (!isTabPressed) return; 

    // shift + tab
    if (e.shiftKey) {
      if (document.activeElement === tabFirstElem) {
        tabLastElem.focus();
        e.preventDefault();
      }
    } 
    // tab
    else {
      if (document.activeElement === tabLastElem) {
        tabFirstElem.focus();
        e.preventDefault();
      }
    }
  }

  handleMouseClickOutside(e) {
    let contentNode = findDOMNode(this.refs.content);

    if (contentNode && !contentNode.contains(e.target)) {
      e.stopPropagation();
      this.props.closePortal();
    }
  }

  handleCloseClick(e) {
    e.preventDefault();
    this.props.closePortal();
  }

  render() {
    const variantClassNames = (this.props.variants || []).map(it => 'modal--' + it);

    // On iOS (and maybe other touch devices), the mousedown event,
    // which is what we're listening to in order to close the modal when the
    // bg is clicked, won't fire unless iOS thinks the element is "clickable".
    // (See: https://developer.apple.com/library/content/documentation/AppleApplications/Reference/SafariWebContent/HandlingEvents/HandlingEvents.html)
    // Now, if we added a mousedown event listener on the component's div, that
    // would trigger iOS to treat all child elements as clickable, but we're
    // instead attaching the listener to the document, which is a little simpler.
    // Therefore, iOS doesn't have a listener linked to a specific element that
    // it can use as a clickability signal. So, to make closing the modal when
    // clicking the bg work on iOS, we have to signal that the bg is clickable
    // some other way. And the easiest way to do that is by making it a button.
    // See: https://developer.mozilla.org/en-US/docs/Web/Events/click#Safari_Mobile
    const modalBg = <button className="modal__bg modal__noFocus" aria-label="Modal No Focus"></button>; // modal__noFocus === do not include in tab focus sequence

    return (
      <div role="dialog" className={classnames("modal", variantClassNames, this.props.className)} id={this.props.id}>
        {modalBg}
        <div className="modal__content" role="dialog" ref="content">
          {!this.props.noCloseButton &&
            <button onClick={this.handleCloseClick} onKeyPress={this.handleCloseClick} aria-label="Close" tabIndex="0" className='icon-close modal__close-icon' autoFocus></button>}
          <section>
            {this.props.children}
          </section>
        </div>
      </div>
    );
  }
}

/**
 * Let's you create modals, by passing in the content as children.
 */
export default (props) => {
  const portalPropNamesAndDefaults = {
    isOpened: undefined,
    onOpen: undefined,
    closeOnEsc: true,
    beforeClose: undefined,
    onClose: undefined,
    onUpdate: undefined
  }

  const modalPropDefaults = {
    closeOnOutsideClick: true,
    noCloseButton: false,
  }

  // Divide props into those for the portal and those for the modal,
  // excluding those to swallow. Anything not in the portalPropNamesAndDefaults
  // is assumed to be a modal prop.
  const dividedProps = Object.entries(props).reduce((prev, [k, v]) => {
    const propType = k in portalPropNamesAndDefaults ? 'portal' : 'modal';
    prev[propType][k] =  v;

    return prev;
  }, {
    portal: {},
    modal: {}
  });

  const isPersistent = typeof props.persistent !== 'undefined' ? props.persistent : false;

  const PortalComponent = isPersistent ? PersistentPortal : Portal;
  const portalProps = {...portalPropNamesAndDefaults, ...dividedProps.portal};
  const modalProps = {...modalPropDefaults, ...dividedProps.modal};

  // If the PortalComponent is the PersistentPortal, it's going to add
  // the open/closed classes to the child directly (it doesn't use a wrapper
  // because we don't want the HTML to be effected by which portal's in use),
  // so it has to know the starting classNames. So we add those as a prop to it,
  // *while still passing them to ModalToPort*.
  if(PortalComponent == PersistentPortal) {
    portalProps.existingWrapperClassName = props.className;
  }

  return (
    <PortalComponent {...portalProps}>
      {/* isOpened is needed for non-persistent tab-trapping */}
      <OverlayAwareModal {...modalProps} isOpened={props.isOpened}>{props.children}</OverlayAwareModal>
    </PortalComponent>
  );
}




// WEBPACK FOOTER //
// ./client/components/Modal/index.js